import React, { useEffect, useState, useRef } from "react";
import { useRecordContext } from "react-admin";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";

const ControlAudio = ({ multiple, data }) => {
  const [audio, setAudio] = useState();
  const [index, setIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const record = useRecordContext();
  const mData = multiple ? (record.files ? record.files : data) : record;

  const isAudio = record ? record.media_class === "Audio" : false;

  const next = (e) => {
    setAutoPlay(true);

    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    let newIndex = index + 1;
    if (newIndex >= mData.length) newIndex = 0;

    setIndex(newIndex);
  };

  useEffect(() => {
    if (multiple && mData) {
      if (mData.length > 0 && index < mData.length) {
        setAudio(mData[index].url);
      }
    } else {
      setAudio(mData && mData.url ? mData.url : undefined);
    }
  }, [index, multiple, mData]);

  if (!isAudio) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 2,
      }}
    >
      <audio
        controls
        autoPlay={autoPlay}
        src={audio}
        preload="none"
        controlsList="nodownload noplaybackrate nofullscreen noremoteplayback"
      />
      {multiple ? (
        <button
          style={{
            border: "none",
            backgroundColor: "transparent",
            cursor: "pointer",
          }}
          onClick={next}
        >
          <SkipNextRoundedIcon />
          <br />
          <span style={{ fontSize: "0.6rem", display: "block" }}>
            {index + 1} / {mData.length}
          </span>
        </button>
      ) : null}
    </div>
  );
};

export default ControlAudio;

const ControlAudioPreview = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const record = useRecordContext();
  const audioRef = useRef(null);

  useEffect(() => {
    const url = record && record.url;
    if (!url || typeof url !== "string") return undefined;

    const audio = new Audio(url);
    audioRef.current = audio;

    const onLoadedMetadata = () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime || 0);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audioRef.current = null;
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    };
  }, [record]);

  const formatTime = (value) => {
    if (!Number.isFinite(value) || value < 0) return "00:00";
    const mins = Math.floor(value / 60);
    const secs = Math.floor(value % 60);
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const togglePlay = React.useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const audio = audioRef.current;
      if (!audio) return;

      try {
        if (isPlaying) {
          audio.pause();
          setIsPlaying(false);
        } else {
          await audio.play();
          setIsPlaying(true);
        }
      } catch (err) {
        setIsPlaying(false);
      }
    },
    [isPlaying]
  );

  if (!record || record.media_class !== "Audio" || !record.url) return null;
  if (record.type && record.type.toLowerCase() !== "mp3") return null;

  const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  return (
    <Tooltip title="Pré-visualização disponível para reprodução antes do download.">
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          minWidth: 210,
          p: 1,
          borderRadius: "12px",
          border: "1px solid rgba(15,23,42,0.12)",
          background: "linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)",
          boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
          <Button
            onClick={togglePlay}
            variant="contained"
            size="small"
            sx={{
              minWidth: 0,
              width: 36,
              height: 36,
              borderRadius: "10px",
              backgroundColor: isPlaying ? "#dc2626" : "#0f766e",
              "&:hover": {
                backgroundColor: isPlaying ? "#b91c1c" : "#0d5f59",
              },
            }}
          >
            {isPlaying ? <PauseCircleOutlineIcon fontSize="small" /> : <PlayCircleOutlineIcon fontSize="small" />}
          </Button>
          <Chip
            size="small"
            label={`${formatTime(currentTime)} / ${formatTime(duration)}`}
            sx={{
              fontWeight: 700,
              fontSize: 11,
              color: "#334155",
              backgroundColor: "#e2e8f0",
              "& .MuiChip-label": { px: 1.1 },
            }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 999,
            backgroundColor: "rgba(15,23,42,0.1)",
            "& .MuiLinearProgress-bar": { borderRadius: 999, backgroundColor: "#0f766e" },
          }}
        />
      </Box>
    </Tooltip>
  );
};

export { ControlAudioPreview };
